import type { InputVariableDecoration } from "@/components/ContentArea/Decorations/MarkdownStyleDecorations";
import type { VariableSet } from "@/components/Playground/types";
import { getVariableValueIsFilled } from "@/components/Playground/utils";
import type { ChatSettingsI } from "@/queries/playground/useUpdatePlaygroundSession";

interface InputVariableDecorationSource {
  activeInputVariableSet: VariableSet | null;
  chat_settings: Partial<ChatSettingsI> | null;
  detectedInputVariables: string[];
  isInlineEditor: boolean;
  isSessionModeChat: boolean;
}

const getVariableValueText = (value: string | object | undefined): string => {
  if (typeof value === "string") {
    return value;
  }

  if (value === undefined) {
    return "";
  }

  return JSON.stringify(value);
};

const getVariablePreview = (value: string | object | undefined): string | undefined => {
  const text = getVariableValueText(value).replace(/\s+/g, " ").trim();
  if (!text) {
    return undefined;
  }

  return ` · ${text.length > 42 ? `${text.slice(0, 42)}…` : text}`;
};

const useInputVariableDecorations = (
  playground: InputVariableDecorationSource,
  readonly: boolean,
): Record<string, InputVariableDecoration> => {
  const {
    activeInputVariableSet: activeSet,
    chat_settings: chatSettings,
    detectedInputVariables,
    isInlineEditor,
    isSessionModeChat,
  } = playground;

  if (isInlineEditor) {
    return {};
  }

  const chatSettingsKeys = new Set(
    Object.values(chatSettings ?? {}).filter((key): key is string => typeof key === "string"),
  );

  return Object.fromEntries(
    detectedInputVariables.map(key => {
      if (readonly) {
        const value = activeSet?.variables.find(variable => variable.key === key)?.value;
        return [key, { state: "filled", preview: getVariablePreview(value) }];
      }

      if (!activeSet) {
        return [key, { state: "no-set" }];
      }

      if (isSessionModeChat && chatSettingsKeys.has(key)) {
        return [key, { state: "filled", preview: " · linked" }];
      }

      const value = activeSet.variables.find(variable => variable.key === key)?.value;

      return [
        key,
        {
          state: getVariableValueIsFilled(value) ? "filled" : "unfilled",
          preview: getVariablePreview(value),
        },
      ];
    }),
  );
};

export default useInputVariableDecorations;
